import { useState, useMemo } from 'react';
import {
  updateTournament,
  updatePlayerOrder,
  addPlayer,
  updatePlayerStatus,
  regenerateUpcoming,
} from '../../api/tournamentsApi';
import useEventMembers from '../../hooks/useEventMembers';
import { Button } from '../UI';
import { useToast } from '../Toast';
import DraftSettings from './DraftSettings';
import TournamentMemberList from './TournamentMemberList';
import { IconArrowLeft, IconTrophy, IconRefresh } from './TournamentIcons';
import {
  FORMAT_LABELS,
  hasAnyMatchPlayed,
  ROUND_AFFECTING_SETTINGS,
  formatSupportsSeeding,
} from './tournamentConstants';

// organizationMemberId активных игроков по seed (null — в конец, тай-брейк по id).
const activeMemberIdsBySeed = (players) => (players || [])
  .filter(p => p.status === 'ACTIVE')
  .slice()
  .sort((a, b) => {
    const sa = a.seed ?? Number.MAX_SAFE_INTEGER;
    const sb = b.seed ?? Number.MAX_SAFE_INTEGER;
    return sa !== sb ? sa - sb : (a.id ?? 0) - (b.id ?? 0);
  })
  .map(p => p.organizationMemberId);

const TournamentEditView = ({ tournament, clientId, organizationId, onSaved, onCancel }) => {
  const { addToast } = useToast();

  // Если хотя бы один матч сыгран — пересчёт раундов невозможен
  const anyMatchPlayed = hasAnyMatchPlayed(tournament);

  // ── Посев (random / manual order) — только Mexicano / Beat the Box ──────────
  // Доступно пока ни один матч не сыгран (порядок влияет лишь на старт).
  // Изменения копятся локально и применяются при сохранении (через regenerateUpcoming).
  const seedingSupported = formatSupportsSeeding(tournament.format);
  const [editRandomOrder, setEditRandomOrder] = useState(tournament.randomSeeding !== false);
  // Предпочтительный порядок активных игроков (organizationMemberId).
  const [editOrder, setEditOrder] = useState(() => activeMemberIdsBySeed(tournament.players));

  // Настройки
  const [courtsCount, setCourtsCount]     = useState(tournament.courtsCount ?? 2);
  const [matchScoring, setMatchScoring]   = useState(tournament.matchScoring ?? 'POINTS');
  const [matchDurationMinutes, setMatchDurationMinutes] = useState(tournament.matchDurationMinutes ?? 15);
  const [rankingMode, setRankingMode]     = useState(tournament.rankingMode ?? 'POINTS');

  // Участники
  const { members: eventMembers, loading: membersLoading, refetch: refetchEventMembers } = useEventMembers(
    tournament.eventPublicId,
    tournament.players,
  );

  // Map organizationMemberId → 'ACTIVE' | 'REMOVED'
  // undefined = участник есть в событии, но не добавлялся в турнир → по умолчанию REMOVED
  const [localStatuses, setLocalStatuses] = useState(() => {
    const map = {};
    (tournament.players || []).forEach(p => {
      map[p.organizationMemberId] = p.status;
    });
    return map;
  });

  const [saving, setSaving] = useState(false);

  const publicId = tournament.publicId;

  const isActive = (memberId) => {
    const status = localStatuses[memberId];
    if (status !== undefined) return status === 'ACTIVE';
    const player = (tournament.players || []).find(p => p.organizationMemberId === memberId);
    return player?.status === 'ACTIVE';
  };

  const toggleMember = (memberId) => {
    const current = isActive(memberId);
    setLocalStatuses(prev => ({ ...prev, [memberId]: current ? 'REMOVED' : 'ACTIVE' }));
  };

  const activeCount = eventMembers.filter(m => isActive(m.organizationMemberId)).length;
  const minPlayers  = (tournament.teamSize || 2) * 2;

  // ── Посев: производные значения чернового порядка ──────────────────────────
  const editActiveMembers = eventMembers.filter(m => isActive(m.organizationMemberId));
  const seedOrderItems = [
    ...editOrder
      .map(id => editActiveMembers.find(m => m.organizationMemberId === id))
      .filter(Boolean),
    ...editActiveMembers.filter(m => !editOrder.includes(m.organizationMemberId)),
  ];
  const currentActiveOrder = seedOrderItems.map(m => m.organizationMemberId);
  const savedActiveOrder   = activeMemberIdsBySeed(tournament.players);

  // Пользователь менял посев (тумблер режима либо ручной порядок) — независимо от
  // того, сыграны ли уже матчи. Нужно и для «пересборки возможна», и для «заблокировано».
  const seedingChanged = seedingSupported && (
    editRandomOrder !== (tournament.randomSeeding !== false) ||
    (!editRandomOrder && currentActiveOrder.join(',') !== savedActiveOrder.join(','))
  );

  // Изменения посева, которые реально ведут к пересборке первого тура
  // (возможны только пока ни один матч не сыгран).
  const seedingDirty = seedingChanged && !anyMatchPlayed;

  const handleReorderPlayers = (orderedItems) =>
    setEditOrder(orderedItems.map(m => m.organizationMemberId));

  // ── Определяем, изменились ли поля, влияющие на пересборку раундов ──────────
  //
  // ROUND_AFFECTING_SETTINGS содержит имена полей настроек из этой группы.
  // Состав игроков тоже влияет на пересборку — проверяем отдельно.
  const roundAffectingChanged = useMemo(() => {
    const currentSettings  = { courtsCount: Number(courtsCount) };
    const originalSettings = { courtsCount: tournament.courtsCount ?? 2 };

    const settingsChanged = [...ROUND_AFFECTING_SETTINGS].some(
      field => currentSettings[field] !== originalSettings[field]
    );
    if (settingsChanged) return true;

    return eventMembers.some(m => {
      const desired  = isActive(m.organizationMemberId) ? 'ACTIVE' : 'REMOVED';
      const existing = (tournament.players || []).find(
        p => p.organizationMemberId === m.organizationMemberId
      );
      return desired !== (existing?.status ?? 'REMOVED');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtsCount, localStatuses, eventMembers]);

  // Нужна пересборка и она возможна (изменился состав/корты или посев)
  const needsRegeneration = !anyMatchPlayed && (roundAffectingChanged || seedingDirty);

  // Нужна пересборка, но матчи уже сыграны → нельзя.
  // Правки состава/кортов и посева (тумблер или порядок) разрешено ВВОДИТЬ,
  // но сохранить нельзя — показываем сообщение и гасим кнопку.
  const regenerationBlocked = anyMatchPlayed && (roundAffectingChanged || seedingChanged);

  const handleSave = async () => {
    if (activeCount < minPlayers) {
      addToast(`Нужно минимум ${minPlayers} активных игроков (сейчас ${activeCount})`, 'error');
      return;
    }
    if (!courtsCount || Number(courtsCount) < 1) {
      addToast('Укажите количество кортов (минимум 1)', 'error');
      return;
    }
    if (matchScoring === 'TIME' && (!matchDurationMinutes || Number(matchDurationMinutes) < 1)) {
      addToast('Укажите длительность матча', 'error');
      return;
    }
    setSaving(true);
    try {
      // 1. Обновить настройки турнира (включая режим посева random/manual)
      const updatedTournament = await updateTournament(publicId, {
        courtsCount: Number(courtsCount),
        matchScoring,
        rankingMode,
        ...(matchScoring === 'TIME'
          ? { matchDurationMinutes: Number(matchDurationMinutes) }
          : { matchDurationMinutes: null }),
        // Режим посева меняется только пока нет сыгранных матчей — иначе бэкенд отклонит.
        ...(seedingSupported && !anyMatchPlayed ? { randomSeeding: editRandomOrder } : {}),
      }, clientId);

      if (needsRegeneration) {
        // 2. Синхронизировать состав
        for (const member of eventMembers) {
          const desired  = isActive(member.organizationMemberId) ? 'ACTIVE' : 'REMOVED';
          const existing = (tournament.players || []).find(
            p => p.organizationMemberId === member.organizationMemberId
          );
          if (!existing) {
            if (desired === 'ACTIVE') {
              await addPlayer(publicId, { organizationMemberId: member.organizationMemberId });
            }
          } else if (existing.status !== desired) {
            await updatePlayerStatus(publicId, existing.id, { status: desired });
          }
        }

        // 2b. Ручной порядок: проставить seed по черновому порядку активных игроков
        if (seedingSupported && !editRandomOrder && currentActiveOrder.length > 0) {
          await updatePlayerOrder(publicId, currentActiveOrder, clientId);
        }

        // 3. Пересчитать раунды
        const regenerated = await regenerateUpcoming(publicId, clientId);
        addToast('Турнир обновлён, раунды пересчитаны', 'success');
        onSaved(regenerated);
      } else {
        // Пересборка не нужна — настройки сохранены, возвращаем актуальный объект
        addToast('Настройки сохранены', 'success');
        onSaved(updatedTournament);
      }
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>

      {/* Header с кнопкой Отмена */}
      <div style={{
        background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
        padding: '14px 16px 16px', color: '#fff', flexShrink: 0,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '5px 10px', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          >
            <IconArrowLeft />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconTrophy />
            </div>
            <div>
              <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Редактирование турнира</h1>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                {FORMAT_LABELS[tournament.format] || tournament.format}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Настройки */}
        <DraftSettings
          courtsCount={courtsCount}             onCourtsCountChange={setCourtsCount}
          matchScoring={matchScoring}           onMatchScoringChange={setMatchScoring}
          matchDurationMinutes={matchDurationMinutes} onMatchDurationChange={setMatchDurationMinutes}
          rankingMode={rankingMode}             onRankingModeChange={setRankingMode}
          rankingLocked={anyMatchPlayed}
          rankingLockedHint="Способ подсчёта нельзя менять — уже есть сыгранные матчи (он влияет на места и на сбор следующих раундов). Сначала очистите результаты."
        />

        {/* Состав */}
        <TournamentMemberList
          members={eventMembers}
          loading={membersLoading}
          isActive={isActive}
          onToggle={toggleMember}
          activeCount={activeCount}
          minPlayers={minPlayers}
          seedingSupported={seedingSupported}
          randomOrder={editRandomOrder}
          onRandomOrderChange={setEditRandomOrder}
          orderItems={seedOrderItems}
          onReorder={handleReorderPlayers}
          reorderDisabled={false}
          reorderHint={anyMatchPlayed
            ? 'Порядок можно изменить, но применить его нельзя — сначала очистите результаты сыгранных матчей.'
            : 'Перетащите за иконку слева или стрелками ↑↓. Применится при сохранении.'}
          eventId={tournament.eventPublicId}
          organizationId={organizationId}
          onParticipantAdded={refetchEventMembers}
        />
      </div>

      {/* Нижняя панель.
          Не фиксируем к низу экрана: на мобильном при открытой клавиатуре
          (ввод кортов, редактирование списка участников) фиксированная панель
          съедала бы и без того малое полезное пространство. Кнопки — обычный
          блок в конце контента, пользователь докручивает до них сам. */}
      <div style={{
        padding: '12px 16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: '#f8fafc',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>

        {/* Пересборка нужна и возможна */}
        {needsRegeneration && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', borderRadius: 10,
            background: '#fefce8', border: '1.5px solid #fde047',
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#713f12' }}>
                Будет выполнен пересчёт раундов
              </div>
              <div style={{ fontSize: 11, color: '#854d0e', marginTop: 1, lineHeight: 1.4 }}>
                Все незавершённые раунды будут пересобраны с учётом новых настроек и состава
              </div>
            </div>
          </div>
        )}

        {/* Пересборка нужна, но невозможна — матчи уже сыграны */}
        {regenerationBlocked && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', borderRadius: 10,
            background: '#fef2f2', border: '1.5px solid #fca5a5',
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>🚫</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>
                Пересчёт раундов невозможен
              </div>
              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 1, lineHeight: 1.4 }}>
                Чтобы изменить эти настройки, сначала очистите результаты сыгранных матчей.
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '12px 18px', borderRadius: 12,
              border: '1.5px solid #d1d5db',
              background: '#fff', color: '#374151',
              fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              flexShrink: 0, opacity: saving ? 0.5 : 1,
            }}
          >
            Отмена
          </button>
          <Button
            variant="primary"
            fullWidth
            loading={saving}
            disabled={activeCount < minPlayers || regenerationBlocked}
            onClick={handleSave}
            style={{
              background: (activeCount >= minPlayers && !regenerationBlocked)
                ? 'linear-gradient(135deg, #d97706, #b45309)'
                : '#e5e7eb',
              color: (activeCount >= minPlayers && !regenerationBlocked) ? '#fff' : '#6b7280',
              boxShadow: 'none',
            }}
          >
            {needsRegeneration
              ? <><IconRefresh /> Сохранить и пересобрать раунды</>
              : 'Сохранить'
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TournamentEditView;
